import { useCallback, useEffect, useState } from "react";
import { CalendarClock } from "lucide-react";
import { theme, btnPrimary, inputStyle, card, type as text } from "../theme";
import { StatusTag } from "./Drawing";
import { api, apiErrorMessage } from "../api/client";
import { fmtDate } from "../lib/format";
import { fmtDateOnly } from "../lib/dateOnly";

// Scheduled board pack (docs/DOCUMENT-TEMPLATES.md "Scheduled board pack", backend lib/boardPack.ts).
// The executive report generated on a set day each month or quarter and e-mailed, as a link, to named
// members. Recipients are members only; a member whose role is limited to some standards cannot receive
// the whole organisation's report and is not offered. Each pack is also a management review input.

interface View {
  schedule: { frequency: "monthly" | "quarterly"; dayOfMonth: number; recipientUserIds: string[]; active: boolean; nextRunAt: string | null } | null;
  runs: Array<{ id: string; periodKey: string; status: string; reason: string | null; recipientCount: number; createdAt: string }>;
  members: Array<{ id: string; name: string; eligible: boolean }>;
}

const ordinal = (n: number) => `${n}${n % 10 === 1 && n !== 11 ? "st" : n % 10 === 2 && n !== 12 ? "nd" : n % 10 === 3 && n !== 13 ? "rd" : "th"}`;

export function BoardPackSchedule() {
  const [view, setView] = useState<View | null>(null);
  const [open, setOpen] = useState(false);
  const [frequency, setFrequency] = useState<"monthly" | "quarterly">("quarterly");
  const [day, setDay] = useState(1);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = (v: View) => {
    setView(v);
    if (v.schedule) { setFrequency(v.schedule.frequency); setDay(v.schedule.dayOfMonth); setRecipients(v.schedule.recipientUserIds); setActive(v.schedule.active); }
  };
  const load = useCallback(async () => {
    try { apply(await api.get<View>("/board-pack")); } catch { setView(null); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function save() {
    setBusy(true); setError(null);
    try { apply(await api.put<View>("/board-pack", { frequency, dayOfMonth: day, recipientUserIds: recipients, active })); setOpen(false); }
    catch (e) { setError(apiErrorMessage(e, "Could not save the schedule.")); }
    finally { setBusy(false); }
  }

  if (!view) return null;
  const s = view.schedule;
  const names = new Map(view.members.map((m) => [m.id, m.name]));
  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ ...text.sectionTitle, margin: 0, display: "flex", alignItems: "center", gap: 6 }}><CalendarClock size={15} /> Scheduled board pack</div>
        {s ? <StatusTag tone={s.active ? "met" : "none"}>{s.active ? "On" : "Paused"}</StatusTag> : <StatusTag tone="none">Not set</StatusTag>}
      </div>
      <div style={{ ...text.hint, marginTop: 4 }}>
        {s
          ? <>Generated {s.frequency === "monthly" ? "every month" : "every quarter"} on the {ordinal(s.dayOfMonth)} and sent to {s.recipientUserIds.map((id) => names.get(id) ?? "a former member").join(", ")}.{s.nextRunAt ? ` Next: ${fmtDateOnly(s.nextRunAt)}.` : ""}</>
          : "Have the executive report generated on a set day each month or quarter and sent, as a link, to the members you name. Each one is also filed as an input to the next management review."}
      </div>
      {view.runs.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {view.runs.slice(0, 4).map((r) => (
            <div key={r.id} style={{ ...text.hint, display: "flex", gap: 8, alignItems: "center" }}>
              <StatusTag tone={r.status === "done" ? "met" : r.status === "skipped" ? "partial" : "gap"}>{r.periodKey}</StatusTag>
              {r.status === "done" ? `Sent to ${r.recipientCount} ${r.recipientCount === 1 ? "member" : "members"} on ${fmtDate(r.createdAt)}` : r.reason ?? r.status}
            </div>
          ))}
        </div>
      )}
      {!open && <div style={{ marginTop: 10 }}><button type="button" style={{ ...btnPrimary, padding: "4px 12px", fontSize: 12 }} onClick={() => { setOpen(true); setError(null); }}>{s ? "Change schedule" : "Set up a schedule"}</button></div>}
      {open && (
        <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <select aria-label="How often" value={frequency} onChange={(e) => setFrequency(e.target.value as "monthly" | "quarterly")} style={{ ...inputStyle, width: 160 }}>
              <option value="monthly">Every month</option>
              <option value="quarterly">Every quarter</option>
            </select>
            <label style={text.hint}>on day
              <input type="number" min={1} max={28} value={day} onChange={(e) => setDay(Number(e.target.value))} style={{ ...inputStyle, width: 70, marginLeft: 6 }} aria-label="Day of the month" />
            </label>
            <label style={{ ...text.hint, display: "flex", alignItems: "center", gap: 6 }}><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Send it</label>
          </div>
          <div>
            <div style={{ ...text.label, marginBottom: 4 }}>Send to</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 4 }}>
              {view.members.map((m) => (
                <label key={m.id} style={{ ...text.body, display: "flex", gap: 6, alignItems: "center", color: m.eligible ? theme.color.text : theme.color.faint }} title={m.eligible ? undefined : "Limited to some standards, so they cannot receive the whole organisation's report"}>
                  <input type="checkbox" disabled={!m.eligible} checked={recipients.includes(m.id)} onChange={(e) => setRecipients((r) => e.target.checked ? [...r, m.id] : r.filter((x) => x !== m.id))} />
                  {m.name}
                </label>
              ))}
            </div>
          </div>
          {error && <div role="alert" style={{ ...text.hint, color: "var(--c-dangerText)" }}>{error}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" disabled={busy} style={{ ...btnPrimary, padding: "4px 12px", fontSize: 12 }} onClick={() => void save()}>Save schedule</button>
            <button type="button" style={{ ...btnPrimary, padding: "4px 12px", fontSize: 12, background: "transparent", color: theme.color.muted, border: `1px solid ${theme.color.border}` }} onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
